import { Link } from "react-router-dom";
import { MarketingPage } from "@/components/marketing/MarketingPage";
import { SampleReport } from "@/components/marketing/SampleReport";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Compass, MessageSquareText, TrendingUp, GraduationCap,
  Building2, ShieldCheck, Star, Users, LineChart, Scale, ClipboardList,
  BarChart3, Layers, BookOpen, Target, Sparkles, Search, Award, Clock, PieChart,
} from "lucide-react";

function Splash({ eyebrow, title, lead, detail, benefits, steps, reportTitle, reportLead, variant, cta }) {
  return (
    <MarketingPage eyebrow={eyebrow} title={title} lead={lead}>
      {detail && (
        <p className="-mt-4 mb-10 max-w-3xl text-base leading-relaxed text-slate-600" data-testid="splash-detail">
          {detail}
        </p>
      )}

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        {benefits.map((b) => (
          <div key={b.title} className="rounded-2xl border border-slate-200 bg-white p-6 card-elevate" data-testid="splash-benefit">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#003057]/6">
              <b.icon className="h-5 w-5 text-[#003057]" />
            </div>
            <h3 className="mt-4 font-heading text-lg font-bold text-[#003057]">{b.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{b.desc}</p>
          </div>
        ))}
      </div>

      {/* SAMPLE REPORT */}
      <div className="mt-16 grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5fa114]">A sample report</p>
          <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-[#003057] sm:text-3xl">{reportTitle}</h2>
          <p className="mt-3 text-base leading-relaxed text-slate-600">{reportLead}</p>
          <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-slate-400">Illustrative · mock data</p>
        </div>
        <SampleReport variant={variant} />
      </div>

      <div className="mt-16 rounded-2xl bg-[#003057] p-8 text-white">
        <h2 className="font-heading text-2xl font-bold">How it works for you</h2>
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <div key={s}>
              <div className="font-mono-num text-2xl font-bold text-[#78be21]">{String(i + 1).padStart(2, "0")}</div>
              <p className="mt-1 text-sm text-slate-300">{s}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12 flex justify-center">
        <Link to="/login">
          <Button size="lg" className="rounded-full bg-[#78be21] px-8 text-base font-bold text-[#00243f] hover:bg-[#6aac1c]">
            {cta} <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </Link>
      </div>
    </MarketingPage>
  );
}

export function ForStudents() {
  return (
    <Splash
      eyebrow="For Students"
      title="See your skills. Steer your future."
      lead="GPA+ shows you the durable skills your coursework is building, how they translate into real careers, and exactly what to strengthen next — with an AI coach in your corner."
      detail="Your transcript proves you passed your classes. It doesn't show an employer that you can communicate under pressure, think critically, or lead a team. GPA+ turns the learning outcomes behind your courses into a clear Transferable Skills Benchmark, then shows you how that profile stacks up against the careers you're considering — so you graduate able to prove what you can actually do."
      benefits={[
        { icon: TrendingUp, title: "Know your benchmark", desc: "A clear Transferable Skills Benchmark across 10 competencies and 3 domains, tracked over time." },
        { icon: Compass, title: "Run what-if scenarios", desc: "Switch degree plans or target careers and instantly see how your fit and matches change." },
        { icon: MessageSquareText, title: "AI Skills Coach", desc: "Ask why you fit a field, what to build next, and how to talk about your strengths in interviews." },
        { icon: GraduationCap, title: "A plan to grow", desc: "Every gap maps to a Learning Academy micro-course you can start right away." },
        { icon: Target, title: "Career alignment", desc: "See your % alignment to 18 career fields, with strengths and gaps for each." },
        { icon: Award, title: "Proof for employers", desc: "Opt in to share a verified skills profile that recruiters can actually trust." },
      ]}
      reportTitle="Your Employment Report, at a glance"
      reportLead="One screen brings together your overall TSB, a radar of all 10 skills, your standout strength, and the single skill worth growing next for your chosen field."
      variant="student"
      steps={[
        "Sign in and view your Employment Report for your chosen field.",
        "Explore what-if plans and ask the AI coach about your fit.",
        "Follow your recommended micro-courses and watch your benchmark rise.",
      ]}
      cta="Try the student demo"
    />
  );
}

export function ForEmployers() {
  return (
    <Splash
      eyebrow="For Employers"
      title="Hire for the skills that actually transfer."
      lead="GPA+ gives you a verifiable, skills-first view of Lone Star talent — opt-in only — and lets you contribute a trusted 360 signal on the interns and employees you work with."
      detail="Employers tell us the same thing again and again: the problem isn't a shortage of applicants, it's a shortage of signal. A GPA can't tell you whether someone will communicate clearly, solve ambiguous problems, or collaborate well. GPA+ surfaces those durable skills, weighted for the roles you actually hire for, and lets you add your own verified observation — so you find the right talent faster and evaluate every candidate on the same fair, transparent basis."
      benefits={[
        { icon: Star, title: "Skills-first talent view", desc: "Browse opt-in student summaries by transferable-skill strengths, not just a GPA." },
        { icon: ShieldCheck, title: "Contribute a verified 360", desc: "Your structured review becomes a labeled, individually-weighted, reversible signal — never a silent overwrite." },
        { icon: Building2, title: "Field-aligned fit", desc: "See readiness weighted for the specific roles and skills you hire for." },
        { icon: Scale, title: "Traceable & fair", desc: "Every score-affecting change is audited. FERPA-aligned and consent-based by design." },
        { icon: Search, title: "Faster screening", desc: "Filter candidates by the competencies that predict success in your environment." },
        { icon: Sparkles, title: "Widen your pipeline", desc: "Skills-first hiring surfaces capable candidates that credential-only filters miss." },
      ]}
      reportTitle="Opt-in talent, ranked by fit"
      reportLead="See each candidate's role-weighted fit, their standout transferable skills, and a clearly-labeled 360 Verified badge when a real workplace has confirmed them."
      variant="employer"
      steps={[
        "Sign in to the employer portal and browse opt-in talent.",
        "Submit a structured 360 on an intern or employee.",
        "Watch the verified signal update — transparently and reversibly.",
      ]}
      cta="Explore the employer demo"
    />
  );
}

export function ForEducators() {
  return (
    <Splash
      eyebrow="For Educators & Advisors"
      title="Advise with evidence, not guesswork."
      lead="GPA+ turns course learning outcomes into a clear picture of each student's durable skills, so faculty and advisors can spot gaps early and plan targeted, timely support."
      detail="Advising too often starts from a transcript and a hunch. GPA+ gives you an evidence base: the durable skills each student is building, where the gaps are, and how their profile aligns with their goals. It complements grades rather than replacing them — a grade certifies content mastery, while the TSB reveals the transferable skills that mastery also builds — so you can turn every advising session into a targeted, data-informed plan."
      benefits={[
        { icon: Users, title: "See your caseload", desc: "Every assigned student's benchmark, level, and weakest skill at a glance." },
        { icon: ClipboardList, title: "Targeted interventions", desc: "Get a suggested next step for each student, mapped to Learning Academy modules." },
        { icon: Layers, title: "Outcomes to skills", desc: "Course SLOs are weight-mapped to the 10 competencies — transparent and traceable." },
        { icon: BookOpen, title: "Ready for D2L", desc: "Built so real Brightspace data can replace the mock feed without changing the model." },
        { icon: Clock, title: "Act early", desc: "Spot at-risk skill gaps before they affect completion or career readiness." },
        { icon: Compass, title: "Guide career fit", desc: "Show students how their skills align with the paths they're weighing." },
      ]}
      reportTitle="Your whole caseload on one screen"
      reportLead="A sortable roster shows each student's TSB, proficiency level, weakest skill, and a concrete recommended next step — so you know exactly who needs what, and when."
      variant="educator"
      steps={[
        "Sign in and open your student caseload.",
        "Drill into a profile to see domain and skill detail.",
        "Recommend the right micro-course and check in next term.",
      ]}
      cta="Explore the advisor demo"
    />
  );
}

export function ForAdministrators() {
  return (
    <Splash
      eyebrow="For Administrators"
      title="Institutional insight you can defend."
      lead="GPA+ rolls individual benchmarks up into cohort analytics — program comparisons, equity-gap views, and a full audit trail — all grounded in transparent, research-backed methodology."
      detail="Leadership needs to connect curriculum to workforce readiness in a way a GPA distribution never could. GPA+ aggregates individual benchmarks into cohort-level insight: how skills are distributed across programs, where systemic gaps sit, and whether every student population is being served equitably. Because the methodology is transparent and every score change is logged, it's insight you can act on and defend — to your board, your accreditors, and your community."
      benefits={[
        { icon: BarChart3, title: "Cohort analytics", desc: "TSB distributions and domain averages across your student population." },
        { icon: LineChart, title: "Program comparisons", desc: "See how programs develop durable skills — and where to invest." },
        { icon: Scale, title: "Equity-gap views", desc: "Surface gaps by first-generation and Pell status to target support." },
        { icon: ShieldCheck, title: "Auditable & compliant", desc: "Every score change is logged. FERPA-aligned access at every level." },
        { icon: PieChart, title: "Strategic alignment", desc: "Tie durable-skill outcomes directly to strategic-plan and workforce goals." },
        { icon: TrendingUp, title: "Track over time", desc: "Monitor how cohorts and programs improve term over term." },
      ]}
      reportTitle="From individuals to institution"
      reportLead="Headline cohort metrics, domain averages, an equity flag that surfaces where a population is trailing, and an audit trail that keeps every score-affecting change traceable."
      variant="administrator"
      steps={[
        "Sign in to the administrator analytics dashboard.",
        "Compare programs and review equity gaps.",
        "Export summaries and track the audit trail for accountability.",
      ]}
      cta="Explore the admin demo"
    />
  );
}
